// Dependency-free natural-language quick-add parser.
//
// Parses a short task string written in German (primary) or English and
// extracts structured fields:
//   - priority   (!dringend / !urgent / !hoch / !high / !mittel / !medium / !niedrig / !low)
//   - dueDate    (heute/today, morgen/tomorrow, übermorgen, weekday names, DD.MM., DD.MM.YYYY)
//   - assignee   (@word)
//   - title      (remaining text after removing recognised tokens)
//   - tokens     (ordered list of every recognised token with its type and raw text)
//
// Design rules:
//   - Multiple priority tokens → last one wins.
//   - Multiple date tokens     → first one wins.
//   - Multiple @mentions       → first one wins.
//   - Never throws; returns a partial result on unexpected input.
//
// Erweiterte Tokens (nur mit `{ extended: true }`, genutzt von der globalen
// Schnellerfassung P2 — QuickAddTask in der Projekttabelle bleibt beim alten
// Verhalten, damit z. B. „Fix #123" dort weiter wörtlich im Titel landet):
//   - projectQuery (#Projekt)  → erstes gewinnt; Auflösung in lib/capture.ts
//   - listQuery    (+Liste)    → erstes gewinnt
//   - time         (10 Uhr, 10:30, um 9:15 Uhr) → erstes gewinnt, "HH:MM"

export type Priority = "low" | "medium" | "high" | "urgent";

export interface TokenRecord {
  type: "priority" | "date" | "assignee" | "project" | "list" | "time";
  raw: string;
}

export interface ParsedQuickAdd {
  title: string;
  priority?: Priority;
  dueDate?: string; // ISO yyyy-mm-dd, local date
  assigneeName?: string;
  /** Nur mit `extended`: Text hinter `#` (ohne das Zeichen). */
  projectQuery?: string;
  /** Nur mit `extended`: Text hinter `+` (ohne das Zeichen). */
  listQuery?: string;
  /** Nur mit `extended`: Uhrzeit als "HH:MM" (24 h). */
  time?: string;
  tokens: TokenRecord[];
}

export interface ParseQuickAddOptions {
  /** #Projekt, +Liste und Uhrzeit zusätzlich erkennen (Schnellerfassung P2). */
  extended?: boolean;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Formats a Date as a local ISO date string (yyyy-mm-dd) without UTC shift. */
function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Returns a new Date set to the next occurrence of `targetWeekday` (0=Sun…6=Sat)
 * strictly after `now`. If today is already that weekday, go to next week.
 */
function nextWeekday(now: Date, targetWeekday: number): Date {
  const today = now.getDay(); // 0=Sun…6=Sat
  let diff = targetWeekday - today;
  if (diff <= 0) diff += 7; // always strictly after today
  const result = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff);
  return result;
}

/** Adds `days` calendar days to `now` (local). */
function addDays(now: Date, days: number): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
}

// ---------------------------------------------------------------------------
// Token pattern definitions
// ---------------------------------------------------------------------------

// Priority: !keyword (case-insensitive), not followed by word chars
const PRIORITY_RE = /!(?:dringend|urgent|hoch|high|mittel|medium|niedrig|low)\b/gi;

const PRIORITY_MAP: Record<string, Priority> = {
  "!dringend": "urgent",
  "!urgent": "urgent",
  "!hoch": "high",
  "!high": "high",
  "!mittel": "medium",
  "!medium": "medium",
  "!niedrig": "low",
  "!low": "low",
};

// Weekday names → JS getDay() value
const WEEKDAY_MAP: Record<string, number> = {
  montag: 1,
  monday: 1,
  dienstag: 2,
  tuesday: 2,
  mittwoch: 3,
  wednesday: 3,
  donnerstag: 4,
  thursday: 4,
  freitag: 5,
  friday: 5,
  samstag: 6,
  saturday: 6,
  sonntag: 0,
  sunday: 0,
};

// Relative keywords — use lookaround instead of \b because ü is not ASCII and
// \b does not match at Unicode word boundaries in JS regex.
const RELATIVE_DATE_RE = /(?<![^\s,;:!?])(übermorgen|heute|today|morgen|tomorrow)(?![^\s,;:!?])/gi;

// Weekday names (German + English)
const WEEKDAY_RE =
  /\b(montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi;

// Assignee: @word (letters, digits, dot, underscore, hyphen)
const ASSIGNEE_RE = /@([\w.\-]+)/gi;
// Erweiterter Modus: Unicode-Namen („@Jürgen") und kein Satzpunkt am Ende
// („@anna." → „anna"); deckungsgleich mit dem iOS-Parser.
const ASSIGNEE_UNICODE_RE = /(?<![\p{L}\p{N}_])@([\p{L}\p{N}_.\-]*[\p{L}\p{N}_])/gu;

// #Projekt / +Liste: nur am Wortanfang (Anfang oder nach Leerraum), damit
// „C#" oder „1+1" nicht greifen. Der Name darf Buchstaben inkl. Umlaute,
// Ziffern, Punkt, Unterstrich und Bindestrich enthalten; „+" braucht als
// erstes Zeichen einen Buchstaben (sonst wäre „+49 …" eine Liste).
const PROJECT_RE = /(?<!\S)#([\p{L}\p{N}_.\-]+)/gu;
const LIST_RE = /(?<!\S)\+(\p{L}[\p{L}\p{N}_.\-]*)/gu;
// Uhrzeit: „10 Uhr", „10:30 Uhr", „um 10:30", „10:30" (optional „um ").
const TIME_UHR_RE = /(?<![\p{L}\p{N}.:])(?:um\s+)?([01]?\d|2[0-3])(?::([0-5]\d))?\s*Uhr(?![\p{L}\p{N}])/giu;
const TIME_COLON_RE = /(?<![\p{L}\p{N}.:])(?:um\s+)?([01]?\d|2[0-3]):([0-5]\d)(?![\p{N}:])/giu;

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

/**
 * Parses a natural-language quick-add string into structured task fields.
 *
 * @param input  The raw user input string.
 * @param now    Reference date for relative date resolution (defaults to new Date()).
 * @returns      A ParsedQuickAdd with title, optional priority/dueDate/assigneeName,
 *               and a tokens array listing every recognised token.
 */
export function parseQuickAdd(
  input: string,
  now: Date = new Date(),
  options: ParseQuickAddOptions = {}
): ParsedQuickAdd {
  // Defensive: treat non-string / empty input gracefully.
  if (typeof input !== "string" || !input) {
    return { title: "", tokens: [] };
  }

  const tokens: TokenRecord[] = [];
  // We track removal spans so we can strip them from the title at the end.
  // Each span: [start, end) in the original `input`.
  const removalSpans: Array<[number, number]> = [];

  let priority: Priority | undefined;
  let dueDate: string | undefined;
  let assigneeName: string | undefined;
  let projectQuery: string | undefined;
  let listQuery: string | undefined;
  let time: string | undefined;

  // ------------------------------------------------------------------
  // 1. Priority tokens — scan all, last one wins
  // ------------------------------------------------------------------
  {
    const re = new RegExp(PRIORITY_RE.source, "gi");
    let m: RegExpExecArray | null;
    while ((m = re.exec(input)) !== null) {
      const raw = m[0];
      const key = raw.toLowerCase();
      const resolved = PRIORITY_MAP[key];
      if (resolved !== undefined) {
        priority = resolved; // last wins
        tokens.push({ type: "priority", raw });
        removalSpans.push([m.index, m.index + raw.length]);
      }
    }
  }

  // ------------------------------------------------------------------
  // 2. Date tokens — first recognised wins
  //    We find all candidates, sort by position, pick the earliest.
  // ------------------------------------------------------------------
  type DateCandidate = { index: number; end: number; raw: string; iso: string };
  const dateCandidates: DateCandidate[] = [];

  // 2a. Relative keywords (heute/morgen/übermorgen)
  {
    const re = new RegExp(RELATIVE_DATE_RE.source, "gi");
    let m: RegExpExecArray | null;
    while ((m = re.exec(input)) !== null) {
      const raw = m[0];
      const lower = raw.toLowerCase();
      let iso: string;
      if (lower === "heute" || lower === "today") {
        iso = toISODate(now);
      } else if (lower === "morgen" || lower === "tomorrow") {
        iso = toISODate(addDays(now, 1));
      } else {
        // übermorgen
        iso = toISODate(addDays(now, 2));
      }
      dateCandidates.push({ index: m.index, end: m.index + raw.length, raw, iso });
    }
  }

  // 2b. Weekday names
  {
    const re = new RegExp(WEEKDAY_RE.source, "gi");
    let m: RegExpExecArray | null;
    while ((m = re.exec(input)) !== null) {
      const raw = m[0];
      const lower = raw.toLowerCase();
      const weekday = WEEKDAY_MAP[lower];
      if (weekday !== undefined) {
        const iso = toISODate(nextWeekday(now, weekday));
        dateCandidates.push({ index: m.index, end: m.index + raw.length, raw, iso });
      }
    }
  }

  // 2c. Explicit dates: DD.MM.YYYY or DD.MM. (short form)
  {
    // Match DD.MM.YYYY or DD.MM. (trailing dot is the short-form separator).
    // We cannot use \b here because \b after a dot fails (dot is non-word char).
    // Instead use negative digit lookarounds to prevent partial matches.
    const re = /(?<!\d)(\d{1,2})\.(\d{1,2})\.(\d{4})?(?!\d)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(input)) !== null) {
      const raw = m[0];
      const dd = parseInt(m[1]!, 10);
      const mm = parseInt(m[2]!, 10);
      const yearStr = m[3];

      // Basic sanity check for month/day ranges
      if (mm < 1 || mm > 12 || dd < 1 || dd > 31) continue;

      let iso: string;
      if (yearStr) {
        // Full DD.MM.YYYY
        const yyyy = parseInt(yearStr, 10);
        iso = `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      } else {
        // Short form DD.MM. — use current year; roll to next year if already past
        const candidate = new Date(now.getFullYear(), mm - 1, dd);
        const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (candidate < todayMidnight) {
          // Past this year → use next year
          iso = `${now.getFullYear() + 1}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
        } else {
          iso = toISODate(candidate);
        }
      }
      dateCandidates.push({ index: m.index, end: m.index + raw.length, raw, iso });
    }
  }

  // Pick the first date candidate by position in input
  if (dateCandidates.length > 0) {
    dateCandidates.sort((a, b) => a.index - b.index);
    const first = dateCandidates[0]!;
    dueDate = first.iso;
    tokens.push({ type: "date", raw: first.raw });
    removalSpans.push([first.index, first.end]);
  }

  // ------------------------------------------------------------------
  // 3. Assignee — first @mention wins
  // ------------------------------------------------------------------
  {
    const re = options.extended
      ? new RegExp(ASSIGNEE_UNICODE_RE.source, "gu")
      : new RegExp(ASSIGNEE_RE.source, "gi");
    let m: RegExpExecArray | null;
    while ((m = re.exec(input)) !== null) {
      const raw = m[0]; // includes the @
      const name = m[1]!;
      if (assigneeName === undefined) {
        assigneeName = name;
        tokens.push({ type: "assignee", raw });
        removalSpans.push([m.index, m.index + raw.length]);
      }
      // only first wins — we still loop to find the token but stop capturing
      break;
    }
  }

  // ------------------------------------------------------------------
  // 3b. Erweiterte Tokens (#Projekt, +Liste, Uhrzeit) — je erstes gewinnt
  // ------------------------------------------------------------------
  if (options.extended) {
    const overlaps = (start: number, end: number): boolean =>
      removalSpans.some(([s, e]) => start < e && end > s);

    const firstMatch = (re: RegExp): RegExpExecArray | null => {
      const r = new RegExp(re.source, re.flags);
      let m: RegExpExecArray | null;
      while ((m = r.exec(input)) !== null) {
        if (!overlaps(m.index, m.index + m[0].length)) return m;
      }
      return null;
    };

    const pm = firstMatch(PROJECT_RE);
    if (pm) {
      projectQuery = pm[1]!;
      tokens.push({ type: "project", raw: pm[0] });
      removalSpans.push([pm.index, pm.index + pm[0].length]);
    }

    const lm = firstMatch(LIST_RE);
    if (lm) {
      listQuery = lm[1]!;
      tokens.push({ type: "list", raw: lm[0] });
      removalSpans.push([lm.index, lm.index + lm[0].length]);
    }

    // „10 Uhr"-Form vor der reinen Doppelpunkt-Form, damit „10:30 Uhr" als
    // Ganzes (inkl. „Uhr") aus dem Titel verschwindet; danach gewinnt die
    // frühere Position.
    const candidates = [firstMatch(TIME_UHR_RE), firstMatch(TIME_COLON_RE)]
      .filter((m): m is RegExpExecArray => m !== null)
      .sort((a, b) => a.index - b.index || b[0].length - a[0].length);
    const tm = candidates[0];
    if (tm) {
      const hh = String(parseInt(tm[1]!, 10)).padStart(2, "0");
      const mm = tm[2] ?? "00";
      time = `${hh}:${mm}`;
      tokens.push({ type: "time", raw: tm[0] });
      removalSpans.push([tm.index, tm.index + tm[0].length]);
    }
  }

  // ------------------------------------------------------------------
  // 4. Build title by removing all recognised token spans
  // ------------------------------------------------------------------
  // Sort spans by start position and remove them from the string.
  removalSpans.sort((a, b) => a[0] - b[0]);

  let title = "";
  let cursor = 0;
  for (const [start, end] of removalSpans) {
    if (start > cursor) {
      title += input.slice(cursor, start);
    }
    cursor = end;
  }
  title += input.slice(cursor);

  // Collapse multiple spaces and trim
  title = title.replace(/\s+/g, " ").trim();

  // If stripping tokens left nothing, fall back to the original trimmed input.
  if (!title) {
    title = input.trim();
  }

  // ------------------------------------------------------------------
  // 5. Sort tokens by position in original input for a clean record
  // ------------------------------------------------------------------
  // (tokens are already appended in discovery order which may not be
  //  positional — re-sort by their index in the original string)
  const tokenPositions = new Map<string, number>();
  {
    // Build a quick lookup: raw → first occurrence index
    for (const tok of tokens) {
      if (!tokenPositions.has(tok.raw)) {
        const idx = input.indexOf(tok.raw);
        tokenPositions.set(tok.raw, idx);
      }
    }
  }
  tokens.sort(
    (a, b) => (tokenPositions.get(a.raw) ?? 0) - (tokenPositions.get(b.raw) ?? 0)
  );

  return {
    title,
    ...(priority !== undefined && { priority }),
    ...(dueDate !== undefined && { dueDate }),
    ...(assigneeName !== undefined && { assigneeName }),
    ...(projectQuery !== undefined && { projectQuery }),
    ...(listQuery !== undefined && { listQuery }),
    ...(time !== undefined && { time }),
    tokens,
  };
}
